import React, { useMemo, useState } from "react";
import {
  Box,
  HStack,
  Text,
  Textarea,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import CopyButton from "components/common/CopyButton";
import JinjaHighlightedTextarea from "components/common/JinjaHighlightedTextarea";
import { modelOptions } from "utils/helperFuncUtils";
import { FiTrash2 } from "react-icons/fi";
import { DarwinAgent, PromptProto } from "build-ts/darwin_agent";
import { DarwinHeader } from "build-ts/common";
import { reasoningEffortLevelToJSON, ReasoningEffortLevel, reasoningEffortLevelFromJSON } from "build-ts/common";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { FaBookOpen, FaLock, FaMagic, FaRegCheckCircle, FaUnlock } from "react-icons/fa";
import { IoDuplicate } from "react-icons/io5";
import { MdNotes } from "react-icons/md";

interface EvaAgentRightPanelProps {
  agent: DarwinAgent;
  onPromptEditorChange: (value: string) => void;
  onPromptConfigChange?: (field: string, value: any) => void;
  isCreateMode?: boolean;
  defaultTimeZone: string;
  temperatureError: string;
  isPromptEditing: boolean;
  assignedPrompts: DarwinHeader[];
  selectedPromptId: string;
  currentPrompt: PromptProto | undefined;
  isLoadingPrompts: boolean;
  isUpdating: boolean;
  // Event handlers
  handlePromptSelection: (promptId: string) => void;
  handleUpdatePrompt: () => void;
  handleOpenValidateModal: () => void;
  handleGeneratePrompt: () => void;
  setIsPromptEditing: (value: boolean) => void;
  setCurrentPrompt: React.Dispatch<React.SetStateAction<PromptProto | undefined>>;
  setTemperatureError: (value: string) => void;
  setShowDeleteConfirmation: (value: boolean) => void;
  setShowDuplicateConfirmation: (value: boolean) => void;
  onViewDocs?: () => void;
}

const EvaAgentRightPanel: React.FC<EvaAgentRightPanelProps> = React.memo(({
  agent,
  onPromptEditorChange,
  onPromptConfigChange,
  isCreateMode = false,
  defaultTimeZone,
  temperatureError,
  isPromptEditing,
  assignedPrompts,
  selectedPromptId,
  currentPrompt,
  isLoadingPrompts,
  isUpdating,
  // Event handlers
  handlePromptSelection,
  handleUpdatePrompt,
  handleOpenValidateModal,
  handleGeneratePrompt,
  setIsPromptEditing,
  setCurrentPrompt,
  setTemperatureError,
  setShowDeleteConfirmation,
  setShowDuplicateConfirmation,
  onViewDocs,
}) => {

  const [isChangelogExpanded, setIsChangelogExpanded] = useState(false);

  // Calculate temperature disable conditions
  const isTemperatureDisabled = useMemo(() => {
    const is5_2Model = currentPrompt?.model_config?.model_name === "gpt-5.2";
    // TODO: reasoning_effort_level is set as string in the Input/Select HTML but it is typed as ReasoningEffortLevel | undefined
    // fix the set value in ReasoningEffort FormControl
    const isReasoningNotNone = String(currentPrompt?.model_config?.reasoning_effort_level) !== reasoningEffortLevelToJSON(ReasoningEffortLevel.NONE);
    const isTemperatureDisabledFor5_2 = is5_2Model && isReasoningNotNone;
    return (!isPromptEditing && !isCreateMode) || isTemperatureDisabledFor5_2;
  }, [isPromptEditing, isCreateMode, currentPrompt]);

  // Early return if currentPrompt is undefined and not in create mode
  if (!currentPrompt && !isCreateMode) {
    return (
      <Box
        width="100%"
        minWidth={0}
        height="100%"
        overflowY="auto"
        css={{
          "&::-webkit-scrollbar": { width: "4px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
        }}
      >
        <Box
          bg="white"
          borderRadius="12px"
          boxShadow="0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)"
          height="max-content"
        >
          <Box p={6} display="flex" gap={4} alignItems="stretch">
            <Text>No prompt selected. Please select a prompt from the dropdown.</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  const versionLabel = !isCreateMode && assignedPrompts.find((p) => String(p.id) === String(selectedPromptId))
    ? (() => {
      const prompt = assignedPrompts.find((p) => String(p.id) === String(selectedPromptId));
      const version = prompt?.version || 1;
      const modifiedDate = prompt?.last_modified_date || 0;
      const isActive = prompt?.is_active || false;
      return `v${version} ${epochConverterWithTimeZone(modifiedDate, "dd-MMM", defaultTimeZone)}${isActive ? " 🟢" : ""}`;
    })()
    : null;

  return (
    <Box position="relative" width="100%" minWidth={0} height="100%" minH={0} overflow="visible">
      {/* Main panel: editor + header + model params - full width */}
      <Box
        width="100%"
        height="100%"
        minH={0}
        display="flex"
        flexDirection="column"
        bg="white"
        borderRadius="lg"
        boxShadow="lg"
        overflow="hidden"
      >
        {/* Header: Prompt Editor + lock + version + actions */}
        <HStack
          justify="space-between"
          align="center"
          px={4}
          py={2}
          borderBottomWidth="1px"
          borderBottomColor="gray.200"
          flexShrink={0}
          flexWrap="wrap"
          gap={2}
        >
          <HStack spacing={3} align="center" flexWrap="wrap">
            <HStack spacing={2}>
              {!isCreateMode && (
                <>
                  <Tooltip label={isPromptEditing ? "Cancel" : "Edit prompt"}>
                    <IconButton
                      aria-label={isPromptEditing ? "Cancel" : "Edit"}
                      icon={isPromptEditing ? <Icon as={FaUnlock} /> : <Icon as={FaLock} />}
                      size="xs"
                      variant="solid"
                      bg={isPromptEditing ? "purple.700" : "red.100"}
                      color={isPromptEditing ? "white" : "red.700"}
                      _hover={{ bg: isPromptEditing ? "purple.600" : "red.200" }}
                      colorScheme="purple"
                      borderRadius="full"
                      onClick={() => (isPromptEditing ? setIsPromptEditing(false) : setIsPromptEditing(true))}
                      isDisabled={!currentPrompt}
                    />
                  </Tooltip>
                </>
              )}
              <Text fontSize="md" color="purple.700" fontWeight="700" mb={0}>
                Prompt
              </Text>
            </HStack>
            {!isCreateMode && (
              <>
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    size="xs"
                    bg="green.50"
                    fontSize="sm"
                    fontWeight="600"
                    isDisabled={isLoadingPrompts || isUpdating}
                    _hover={{ bg: "green.100" }}
                    _active={{ bg: "green.200" }}
                    px={3}
                    py={2}
                    color="black"
                    borderWidth="1px"
                    borderColor="green.600"
                  >
                    {isLoadingPrompts ? "Loading..." : versionLabel || "Select version"}
                  </MenuButton>
                  <MenuList maxH="250px" overflowY="auto" px={0}>
                    {assignedPrompts.map((prompt, index) => {
                      const promptId = prompt.id || "";
                      const isActive = prompt.is_active || false;
                      const modifiedDate = prompt.last_modified_date || 0;
                      const version = prompt.version || 1;
                      return (
                        <MenuItem
                          key={promptId || `prompt-${index}`}
                          value={String(promptId)}
                          onClick={() => handlePromptSelection(promptId)}
                          fontWeight="600"
                          fontSize="sm"
                          bg={selectedPromptId === String(promptId) ? "green.100" : undefined}
                        >
                          {`v${version} ${epochConverterWithTimeZone(modifiedDate || 0, "dd-MMM-yyyy", defaultTimeZone)}${isActive ? " 🟢" : ""}`}
                        </MenuItem>
                      );
                    })}
                  </MenuList>
                </Menu>
                {(assignedPrompts.filter((p: DarwinHeader) => p.is_active)[0]?.id === currentPrompt?.header?.id) ? (
                  <HStack spacing={1} px={2} py={2} bg="green.600" borderRadius="full">
                    <Icon as={FaRegCheckCircle} boxSize={4} color="white" />
                  </HStack>
                ) : (

                  <Switch
                    isChecked={currentPrompt?.header?.is_active || false}
                    onChange={(e) => {
                      const isActive = e.target.checked;
                      setCurrentPrompt(prev => prev ? { ...prev, header: { ...prev.header, is_active: isActive } } : prev);
                      if (onPromptConfigChange) onPromptConfigChange("header.is_active", isActive);
                    }}
                    colorScheme="green"
                    isDisabled={!isPromptEditing && !isCreateMode}
                    size="md"
                    sx={{
                      "& .chakra-switch__track:not([data-checked])": {
                        bg: "gray.400",
                        borderWidth: "1px",
                        borderColor: "gray.500",
                      },
                    }}
                    mb={0}
                  />

                )}

              </>
            )}
          </HStack>
          <HStack spacing={2}>
            <Button
              size="xs"
              variant="solid"
              leftIcon={<Icon as={MdNotes} />}
              onClick={() => setIsChangelogExpanded((v) => !v)}
              colorScheme="orange"
              bg="orange.600"
              color="white"
              _hover={{ bg: "orange.500" }}
              fontWeight="700"
            >
              Changelog
            </Button>
            <Button
              leftIcon={<FaBookOpen />}
              size="xs"
              variant="solid"
              onClick={onViewDocs}
              colorScheme="purple"
              bg="purple.700"
              color="white"
              _hover={{ bg: "purple.600" }}
              fontWeight="700"
            >
              Jinja
            </Button>
            <Button
              leftIcon={<FaMagic />}
              variant="solid"
              size="xs"
              onClick={handleGeneratePrompt}
              colorScheme="purple"
              bg="blue.600"
              color="white"
              _hover={{ bg: "blue.500" }}
              fontWeight="700"
            >
              Generate
            </Button>
            <Tooltip label="Delete">
              <IconButton
                aria-label="Delete prompt"
                icon={<Icon as={FiTrash2} />}
                size="xs"
                variant="outline"
                colorScheme="red"
                onClick={() => setShowDeleteConfirmation(true)}
                isDisabled={!currentPrompt}
              />
            </Tooltip>
            <CopyButton value={currentPrompt?.template} />
            <Tooltip label="Duplicate">
              <IconButton
                aria-label="Duplicate prompt"
                icon={<Icon as={IoDuplicate} boxSize={5} />}
                size="xs"
                variant="outline"
                colorScheme="purple"
                onClick={() => setShowDuplicateConfirmation(true)}
                isDisabled={!currentPrompt}
              />
            </Tooltip>

          </HStack>
        </HStack>

        {/* Editor - main content */}
        <Box flex={1} minH={0} display="flex" flexDirection="column" px={1} py={1} overflow="hidden">
          <Box flex={1} minH={0} w="100%">
            <JinjaHighlightedTextarea
              value={currentPrompt?.template || ""}
              onChange={(value) => {
                onPromptEditorChange(value);
                setCurrentPrompt(prev => ({ ...prev, template: value }));
                if (onPromptConfigChange) onPromptConfigChange("template", value);
              }}
              placeholder="Enter your system prompt here. Use Jinja template syntax for variables like {{agent_name}}, {{plan_name}}, {{MKG_INFO}}, etc."
              isReadOnly={!isPromptEditing && !isCreateMode}
            />
          </Box>
        </Box>

        {/* Bottom row: Model params - full width, compact inline labels */}
        <HStack
          width="100%"
          justify="flex-start"
          align="center"
          px={4}
          py={1}
          borderTopWidth="1px"
          borderTopColor="gray.200"
          flexShrink={0}
          flexWrap="wrap"
          gap={6}
        >
          <FormControl display="flex" alignItems="center" flex="1" minW="180px">
            <FormLabel fontSize="xs" mb={0} mr={2} fontWeight="700" whiteSpace="nowrap">Model</FormLabel>
            <Select
              value={`${currentPrompt?.model_config?.model_provider}:${currentPrompt?.model_config?.model_name}`}
              onChange={(e) => {
                const [provider, modelName] = e.target.value.split(":");
                const reasoningOptions = modelOptions[provider as keyof typeof modelOptions]?.find(m => m.model_name === modelName && (m.model_options as any)?.reasoning?.length > 0);
                const defaultReasoningLevel = reasoningOptions
                  ? reasoningEffortLevelFromJSON((reasoningOptions.model_options as any)?.reasoning?.[0])
                  : ReasoningEffortLevel.REASONING_NOT_VALID;
                setCurrentPrompt(prev => prev ? { ...prev, model_config: { ...prev.model_config, model_name: modelName, model_provider: provider, reasoning_effort_level: defaultReasoningLevel } as any } : prev);
                if (onPromptConfigChange) {
                  onPromptConfigChange("model_config.model_name", modelName);
                  onPromptConfigChange("model_config.model_provider", provider);
                  onPromptConfigChange("model_config.reasoning_effort_level", defaultReasoningLevel);
                }
              }}
              isDisabled={!isPromptEditing && !isCreateMode}
              size="sm"
              bg="white"
              borderColor="gray.300"
              flex={1}
            >
              {Object.entries(modelOptions).map(([provider, models]) => (
                <optgroup key={provider} label={provider.replace("_", " ").toUpperCase()}>
                  {models.map(({ model_name }) => (
                    <option key={`${provider}:${model_name}`} value={`${provider}:${model_name}`}>
                      {model_name}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
          </FormControl>
          <FormControl display="flex" alignItems="center" w="auto" isInvalid={!!temperatureError}>
            <FormLabel fontSize="xs" mb={0} mr={2} fontWeight="700" whiteSpace="nowrap">Temp</FormLabel>
            <Input
              size="sm"
              type="number"
              step="0.1"
              w="60px"
              value={currentPrompt?.model_config?.temperature?.toString() || ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === "") {
                  setTemperatureError("");
                  setCurrentPrompt(prev => prev ? { ...prev, model_config: { ...prev.model_config, temperature: 0 } } : prev);
                  if (onPromptConfigChange) onPromptConfigChange("model_config.temperature", 0);
                  return;
                }
                const numValue = parseFloat(inputValue);
                if (isNaN(numValue)) { setTemperatureError("Invalid"); return; }
                if (numValue < 0 || numValue > 2) { setTemperatureError("0-2"); return; }
                setTemperatureError("");
                setCurrentPrompt(prev => prev ? { ...prev, model_config: { ...prev.model_config, temperature: numValue } } : prev);
                if (onPromptConfigChange) onPromptConfigChange("model_config.temperature", numValue);
              }}
              onWheel={(e) => {
                if (isTemperatureDisabled) return;
                e.preventDefault();
                e.stopPropagation();
                const currentValue = currentPrompt?.model_config?.temperature ?? 0;
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                const newValue = Math.round((currentValue + delta) * 10) / 10;
                const clampedValue = Math.max(0, Math.min(2, newValue));
                if (clampedValue !== currentValue) {
                  setTemperatureError("");
                  setCurrentPrompt(prev => prev ? { ...prev, model_config: { ...prev.model_config, temperature: clampedValue } } : prev);
                  if (onPromptConfigChange) onPromptConfigChange("model_config.temperature", clampedValue);
                }
              }}
              placeholder="0"
              bg="white"
              borderColor="gray.300"
              isDisabled={isTemperatureDisabled}
              color={isTemperatureDisabled ? "gray.400" : "black"}
            />
            {temperatureError && <Text fontSize="xs" color="red.500" ml={1}>{temperatureError}</Text>}
          </FormControl>
          {modelOptions[(currentPrompt?.model_config?.model_provider || "") as keyof typeof modelOptions]?.some(m => m.model_name === currentPrompt?.model_config?.model_name && (m.model_options as any)?.reasoning?.length > 0) && (
            <FormControl display="flex" alignItems="center" w="auto">
              <FormLabel fontSize="xs" mb={0} mr={2} fontWeight="700" whiteSpace="nowrap">Reasoning</FormLabel>
              <Select
                value={reasoningEffortLevelToJSON(reasoningEffortLevelFromJSON((currentPrompt?.model_config as any)?.reasoning_effort_level) ?? ReasoningEffortLevel.REASONING_NOT_VALID)}
                onChange={(e) => {
                  const value = e.target.value;
                  setCurrentPrompt(prev => prev ? { ...prev, model_config: { ...prev.model_config, reasoning_effort_level: reasoningEffortLevelFromJSON(value) } as any } : prev);
                  if (onPromptConfigChange) onPromptConfigChange("model_config.reasoning_effort_level", reasoningEffortLevelFromJSON(value));
                }}
                isDisabled={!isPromptEditing && !isCreateMode}
                size="sm"
                bg="white"
                borderColor="gray.300"
                w="120px"
              >
                {((modelOptions[currentPrompt?.model_config?.model_provider as keyof typeof modelOptions] || [])?.find((m: any) => m.model_name === currentPrompt?.model_config?.model_name)?.model_options as any)?.reasoning?.map((level: ReasoningEffortLevel) => (
                  <option key={reasoningEffortLevelToJSON(level)} value={reasoningEffortLevelToJSON(level)}>
                    {reasoningEffortLevelToJSON(level).replace(/_/g, " ").toUpperCase()}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          {isPromptEditing && (
            <HStack spacing={2} ml="auto">
              <Button size="sm" variant="outline" onClick={() => setIsPromptEditing(false)}>
                Cancel
              </Button>
              <Button size="sm" colorScheme="green" onClick={() => handleUpdatePrompt()} isLoading={isUpdating}>
                Save
              </Button>
            </HStack>
          )}
        </HStack>
      </Box>

      {/* Changelog overlay - floats over content */}
      {isChangelogExpanded && (
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          w="350px"
          bg="white"
          boxShadow="xl"
          borderLeftWidth="1px"
          borderLeftColor="gray.200"
          borderRadius="lg"
          borderRightRadius="lg"
          zIndex={10}
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <HStack justify="space-between" align="center" px={4} py={1} borderBottomWidth="1px" borderBottomColor="gray.200" flexShrink={0} bg="orange.50">
            <HStack spacing={2}>
              <Icon as={MdNotes} boxSize={5} color="orange.700" />
              <Text fontSize="sm" fontWeight="700" mb={0} color="orange.700">Prompt Version Changelog</Text>
            </HStack>
            <IconButton
              aria-label="Close changelog"
              icon={<Icon as={ChevronRightIcon} />}
              size="sm"
              variant="ghost"
              onClick={() => setIsChangelogExpanded(false)}
              colorScheme="orange"
            />
          </HStack>
          <Box flex={1} display="flex" flexDirection="column" minH={0} p={2}>
            <Textarea
              name="prompt description"
              value={currentPrompt?.header?.description || ""}
              onChange={(e) => {
                const value = e.target.value;
                setCurrentPrompt(prev => prev ? { ...prev, header: { ...prev.header, description: value } } : prev);
                if (onPromptConfigChange) onPromptConfigChange("header.description", value);
              }}
              placeholder="Prompt description / changelog..."
              borderColor="gray.200"
              fontSize="sm"
              lineHeight="tall"
              resize="none"
              flex={1}
              minH={0}
              bg="white"
              color="gray.800"
              _placeholder={{ color: "gray.400" }}
              isDisabled={!isPromptEditing && !isCreateMode}
              _disabled={{ opacity: 1, color: "gray.800", cursor: "not-allowed" }}
              sx={{
                "&[readonly]": { opacity: 1, bg: "white", cursor: "default" },
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { background: "gray.100", borderRadius: "3px" },
                "&::-webkit-scrollbar-thumb": { background: "gray.300", borderRadius: "3px" },
              }}
            />
            {!isCreateMode && currentPrompt?.header?.last_modified_date && (
              <Text fontSize="sm" color="gray.700" mt={3} flexShrink={0} textAlign="left" mb={0}>
                Last modified: {epochConverterWithTimeZone(currentPrompt.header.last_modified_date, "dd MMM yyyy, hh:mm a", defaultTimeZone)}
              </Text>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default EvaAgentRightPanel;
